import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { CurrencyDollar, CircleNotch, Robot, Phone, ChatCircleDots, Lightning, TrendUp, Coins, Receipt } from "@phosphor-icons/react";
import { modelLabel } from "@/lib/models";

const fmtTokens = (n) => (n || 0).toLocaleString("sv-SE");
const fmtCost = (n) => (n || 0).toLocaleString("sv-SE", { minimumFractionDigits: n < 1 ? 2 : 2, maximumFractionDigits: n < 1 ? 3 : 2 }) + " kr";

const TYPE_META = {
  voice: { label: "Röst", Icon: Phone, color: "#7c3aed" },
  chat: { label: "Chatt", Icon: ChatCircleDots, color: "#0033FF" },
  autopilot: { label: "Autopilot", Icon: Robot, color: "#0d9488" },
};

function StatCard({ icon: Icon, label, value, sub, testid, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-zinc-400">
        <Icon size={16} weight="fill" style={{ color: accent || "#0033FF" }} /> {label}
      </div>
      <div className="mt-2 font-display font-extrabold text-2xl tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-400">{sub}</div>}
    </div>
  );
}

export default function Costs() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/usage").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (data === null) {
    return <div className="p-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>;
  }
  if (data === false) {
    return <div className="p-10 text-center text-zinc-500" data-testid="costs-error">Kunde inte ladda förbrukningsdata.</div>;
  }

  const { totals, month, agents, daily, alerts } = data;
  const maxDayCost = Math.max(0.0001, ...(daily || []).map((d) => d.cost));
  const hasData = (totals?.requests || 0) > 0;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="costs-page">
      <div>
        <h1 className="font-display font-extrabold text-2xl flex items-center gap-2">
          <CurrencyDollar size={26} weight="fill" className="text-[#0033FF]" /> Kostnader & förbrukning
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Uppskattad AI-kostnad per agent och månad. Siffrorna är vägledande estimat baserade på tokenförbrukning.</p>
      </div>

      {/* high-usage alerts */}
      {(alerts || []).length > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm" data-testid="costs-alerts">
          <Lightning size={18} weight="fill" className="mt-0.5 shrink-0" />
          <span>
            <strong>{alerts.map((a) => a.name).join(", ")}</strong> står för en stor del av kostnaden. Överväg en billigare modell (t.ex. Senzai 5.4 Mini) eller kortare svar för att minska förbrukningen.
          </span>
        </div>
      )}

      {/* stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Receipt} label="Denna månad" value={fmtCost(month?.cost)} sub={`${fmtTokens(month?.tokens)} tokens · ${fmtTokens(month?.requests)} svar`} testid="stat-month-cost" />
        <StatCard icon={Coins} label="Tokens denna månad" value={fmtTokens(month?.tokens)} sub="Förbrukade in + ut" testid="stat-month-tokens" accent="#7c3aed" />
        <StatCard icon={CurrencyDollar} label="Total kostnad" value={fmtCost(totals?.cost)} sub="Sedan start" testid="stat-total-cost" accent="#0d9488" />
        <StatCard icon={TrendUp} label="Antal AI-svar" value={fmtTokens(totals?.requests)} sub="Alla agenter, all tid" testid="stat-total-requests" accent="#d97706" />
      </div>

      {!hasData ? (
        <div className="bg-white border border-zinc-200 rounded-xl py-16 text-center" data-testid="costs-empty">
          <div className="mx-auto h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center">
            <CurrencyDollar size={24} weight="duotone" className="text-[#0033FF]" />
          </div>
          <h3 className="mt-4 font-display font-bold text-lg">Ingen förbrukning än</h3>
          <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">Så fort dina agenter börjar svara på meddelanden och samtal visas kostnaden här.</p>
        </div>
      ) : (
        <>
          {/* daily cost chart */}
          {(daily || []).length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="costs-daily-chart">
              <h2 className="text-sm font-bold text-zinc-700 mb-4">Kostnad senaste {daily.length} dagarna</h2>
              <div className="flex items-end gap-1.5 h-32">
                {daily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end group">
                    <div className="w-full rounded-t bg-[#0033FF]/80 hover:bg-[#0033FF] transition-all relative"
                      style={{ height: `${Math.max(3, (d.cost / maxDayCost) * 100)}%` }}>
                      <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-zinc-700 bg-white border border-zinc-200 rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 whitespace-nowrap shadow-sm pointer-events-none">
                        {fmtCost(d.cost)}
                      </span>
                    </div>
                    <span className="mt-1 text-[9px] text-zinc-400">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* per-agent table */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6">
            <h2 className="text-sm font-bold text-zinc-700 mb-4">Per agent</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="costs-agent-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-zinc-400 border-b border-zinc-100">
                    <th className="py-2 pr-4 font-bold">Agent</th>
                    <th className="py-2 pr-4 font-bold">Modell</th>
                    <th className="py-2 pr-4 font-bold text-right">Svar</th>
                    <th className="py-2 pr-4 font-bold text-right">Tokens</th>
                    <th className="py-2 pr-4 font-bold text-right">~ Kostnad/samtal</th>
                    <th className="py-2 pr-4 font-bold text-right">Denna månad</th>
                    <th className="py-2 font-bold text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a) => {
                    const meta = TYPE_META[a.type] || TYPE_META.chat;
                    return (
                      <tr key={a.id} data-testid={`cost-row-${a.id}`} className="border-b border-zinc-50 hover:bg-zinc-50/60">
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}1A` }}>
                              <meta.Icon size={15} weight="fill" style={{ color: meta.color }} />
                            </span>
                            <div className="min-w-0">
                              <div className="font-semibold truncate flex items-center gap-1.5">
                                {a.name}
                                {a.high_usage && <span className="text-[9px] font-bold uppercase text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">Hög</span>}
                              </div>
                              <div className="text-[11px] text-zinc-400">{meta.label}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 pr-4"><span className="font-mono text-xs text-zinc-500">{a.model ? modelLabel(a.model) : "—"}</span></td>
                        <td className="py-3 pr-4 text-right text-zinc-600">{fmtTokens(a.requests)}</td>
                        <td className="py-3 pr-4 text-right text-zinc-600">{fmtTokens(a.tokens)}</td>
                        <td className="py-3 pr-4 text-right font-semibold">{fmtCost(a.est_call_cost)}</td>
                        <td className="py-3 pr-4 text-right text-zinc-600">{fmtCost(a.month_cost)}</td>
                        <td className="py-3 text-right font-semibold">{fmtCost(a.cost)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-zinc-400">"~ Kostnad/samtal" är ett estimat (röst ≈ 12 repliker, chatt ≈ 6 repliker per samtal). Faktisk kostnad debiteras via din Universal Key.</p>
          </div>
        </>
      )}
    </div>
  );
}
